/**
 * The licence panel for the Chakra-based importers — the ones that render the
 * shared `ImporterFlow`. The plan, the month's count, a place for a key, and
 * the way to buy, in the same words the kit-based plugins use
 * (`@figma-importers/licensing/preact`).
 *
 * "Buy Pro" is a button, not a link: a hosted plugin UI cannot open a URL (the
 * iframe has no `allow-popups`), so the click asks the host to send the sandbox
 * a link NAME, which it looks up before calling `figma.openExternal`.
 */

import { Box, Input, Stack, Text } from '@chakra-ui/react';
import { useState, useSyncExternalStore } from 'react';
import type { Gate, LicenseLinkName, LicenseUser, Licensing } from '@figma-importers/licensing';
import { Button } from './Button';

/**
 * `useSyncExternalStore` compares each snapshot to the last with `Object.is` and re-renders
 * when they differ, so `getSnapshot` MUST return a stable reference for unchanged state. A
 * no-argument `licensing.gate()` does: it is cached and invalidated by `subscribe`'s own
 * notify. Passing a request here would not, and would loop — see error #185 in
 * `packages/licensing`'s tests.
 */
function useGate(licensing: Licensing): Gate {
  return useSyncExternalStore(
    (listener) => licensing.subscribe(listener),
    () => licensing.gate(),
    () => licensing.gate(),
  );
}

export interface LicensePanelProps {
  licensing: Licensing;
  user: LicenseUser | null;
  /** Ask the sandbox to open one of the licence links; it is the only half that can. */
  onOpenLink: (name: LicenseLinkName) => void;
  /** Shown when an import was just refused; the panel then leads with the reason. */
  blocked?: boolean;
}

export function LicensePanel({ licensing, user, onOpenLink, blocked = false }: LicensePanelProps) {
  const gate = useGate(licensing);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entering, setEntering] = useState(false);

  const activate = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const result = await licensing.activate(key, user);
    setBusy(false);
    if (result.ok) setKey('');
    else setError(result.error);
  };

  if (gate.plan === 'pro') {
    return (
      <Stack gap="2">
        <Text fontSize="2xs" color="figma.text">
          <Text as="span" fontWeight="500">Pro</Text>
          <Text as="span" color="figma.textMuted"> · unlimited imports here and in every tomake.design plugin.</Text>
        </Text>
        <Button colorPalette="gray" variant="outline" onClick={() => void licensing.deactivate(user)}>
          Deactivate on this account
        </Button>
      </Stack>
    );
  }

  /** The key field, shared by the beta panel and the free one. */
  const keyEntry = (
    <Stack gap="2">
      <Text fontSize="2xs" color="figma.textMuted">Already bought one? Enter the key from your receipt.</Text>
      <Input
        size="sm"
        value={key}
        placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
        disabled={busy}
        onChange={(event) => setKey(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !busy && key.trim().length > 0) void activate();
        }}
      />
      {error ? <Text fontSize="2xs" color="figma.text">{error}</Text> : null}
      <Button disabled={busy || key.trim().length === 0} onClick={() => void activate()}>
        {busy ? 'Checking…' : 'Activate licence'}
      </Button>
      {user === null ? (
        <Text fontSize="2xs" color="figma.textMuted">
          Sign in to Figma so the licence can be tied to your account and follow you to the other plugins.
        </Text>
      ) : null}
    </Stack>
  );

  /*
   * BETA: no count, no limit, nothing to buy. Mirrors the kit panel in
   * `@figma-importers/licensing/preact`; illustrator-to-figma is the app that uses this one.
   *
   * The free line below would read "N of 5 imports left this month", which is untrue when
   * nothing is counted, and Buy Pro would be selling a limit that does not exist. The key
   * field stays reachable so a licence bought for another plugin can be entered here and
   * survives this app leaving beta.
   */
  if (gate.plan === 'beta') {
    return (
      <Stack gap="2">
        <Text fontSize="2xs" color="figma.textMuted">
          <Text as="span" fontWeight="500" color="figma.text">Beta</Text>
          {' · free while this importer is in beta, with no monthly limit.'}
        </Text>
        {entering ? null : (
          <Button colorPalette="gray" variant="outline" onClick={() => setEntering(true)}>
            I have a licence key
          </Button>
        )}
        {entering ? keyEntry : null}
      </Stack>
    );
  }

  const showKeyEntry = entering || blocked;

  return (
    <Stack gap="2">
      {blocked && gate.reason ? (
        <Text fontSize="2xs" color="figma.text">{gate.reason}</Text>
      ) : null}

      <Text fontSize="2xs" color="figma.textMuted">
        <Text as="span" fontWeight="500" color="figma.text">Free</Text>
        {` · ${gate.remaining} of ${gate.limit} imports left this month. One Pro licence unlocks every tomake.design plugin.`}
      </Text>

      <Button colorPalette="gray" variant="outline" onClick={() => onOpenLink('checkout')}>Buy Pro</Button>

      {showKeyEntry ? (
        keyEntry
      ) : (
        <Box>
          <Button colorPalette="gray" variant="outline" onClick={() => setEntering(true)}>I have a licence key</Button>
        </Box>
      )}
    </Stack>
  );
}
